import DownloadIcon from '@mui/icons-material/Download'
import { Button, ButtonProps } from '@mui/material'
import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

interface CsvExportButtonProps extends Omit<ButtonProps, 'onClick'> {
  filename: string
  headers?: (string | number)[]
  rows: (string | number)[][]
  startIcon?: ReactNode
}

const toCsvValue = (value: string | number) => {
  const normalized = String(value)
    .replace(/\r?\n/g, ' ')
    .replace(/\u00a0/g, ' ')
    .trim()
  const needsEscaping = normalized.includes(',') || normalized.includes('"')
  if (!needsEscaping) {
    return normalized
  }
  const escaped = normalized.replace(/"/g, '""')
  return `"${escaped}`.concat('"')
}

export const CsvExportButton = ({
  filename,
  headers = [],
  rows,
  disabled,
  startIcon,
  children,
  ...buttonProps
}: CsvExportButtonProps) => {
  const { t } = useTranslation()
  const handleExport = () => {
    if (!headers.length && !rows.length) {
      return
    }

    const csvRows = headers.length ? [headers, ...rows] : rows
    const csvContent = csvRows.map((row) => row.map(toCsvValue).join(',')).join('\r\n')

    const blob = new Blob(['\ufeff', csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  return (
    <Button {...buttonProps} startIcon={startIcon ?? <DownloadIcon />} onClick={handleExport} disabled={disabled}>
      {t('common.exportCsv')}
      {children}
    </Button>
  )
}
